import { useCookies } from 'react-cookie'

import { useActionless, useAppSelector } from 'app-redux/hooks'
import { SET_ACTIVE_STATE, TOGGLE_MODAL } from 'app-redux/active/actions/types'
import { SET_DECK_STATE } from 'app-redux/deck/actions/types'

import ColorTagService from 'services/colorTag.service'
import ToastService from 'services/toast.service'

import SimpleSpinner from 'components/elements/SimpleSpinner'

import { Option } from 'components/elements/ArchidektDropdown/types'
import { GlobalColorTag } from 'services/apiTypes/colorTag.types'

import { sortColorTags, sortColorTagList } from 'utils/sortColorTags'

import styles from './useDeckColorTagOptions.module.scss'

const useDeckColorTagOptions = (callback: (selectedTag: GlobalColorTag) => void): [Option[], () => Promise<any>] => {
  const [{ tbId: userId }] = useCookies(['tbId'])

  const colorLabels = useAppSelector(state => state.deck.colorLabels)
  const globalColorTags = useAppSelector(state => state.active.globalColorTags)

  const [toggleModal, setActiveState, setDeckState] = useActionless(TOGGLE_MODAL, SET_ACTIVE_STATE, SET_DECK_STATE)

  const handleFetchGlobalColorTags = (): Promise<any> => {
    if (!userId || globalColorTags !== null) return Promise.resolve()

    return ColorTagService.list()
      .then(res => setActiveState({ globalColorTags: res }))
      .catch(() => ToastService.create('Unable to load global color tags', 'Deck page', 'error'))
  }

  const handleCallback = (tag: GlobalColorTag) => {
    callback(tag)

    if (colorLabels.some(deckTag => deckTag.name === tag.name && deckTag.color === tag.color)) return

    setDeckState({ colorLabels: [...colorLabels, tag] })
  }

  const visibleColorLabels = (globalColorTags || []).filter(
    tag => !colorLabels.some(label => label.name === tag.name && label.color === tag.color),
  )
  const visibleColorLabelsSorted = sortColorTagList(visibleColorLabels)

  const { defaultColorTags, userColorTagsSorted } = sortColorTags(colorLabels)

  const options: Option[] = [
    ...defaultColorTags.map(tag => ({
      label: (
        <div className={styles.inline}>
          <span className={styles.splotch} style={{ backgroundColor: tag.color }} />
          <span className={styles.tagName}>{tag.name || 'Default color tag'}</span>
        </div>
      ),
      id: `${tag.name},${tag.color}`,
      onClick: () => handleCallback(tag),
    })),
    { type: 'spacer', hidden: !userColorTagsSorted.length },
    ...userColorTagsSorted.map(tag => ({
      label: (
        <div className={styles.inline}>
          <span className={styles.splotch} style={{ backgroundColor: tag.color }} />
          <span className={styles.tagName}>{tag.name || 'Default color tag'}</span>
        </div>
      ),
      id: `${tag.name},${tag.color}`,
      onClick: () => handleCallback(tag),
    })),
    { type: 'spacer' },
    {
      id: 'fetching-color-tag-templates',
      label: (
        <div className={styles.inline}>
          <SimpleSpinner size="xSmall" /> Color tag templates
        </div>
      ),
      onClick: () => null,
      disabled: true,
      hidden: Array.isArray(globalColorTags),
    },
    {
      id: 'no-color-tag-templates',
      label: 'No color tag templates',
      onClick: () => null,
      disabled: true,
      hidden: Array.isArray(globalColorTags) && !!globalColorTags.length,
    },
    ...visibleColorLabelsSorted?.map(tag => ({
      label: (
        <div className={styles.inline}>
          <span className={styles.splotch} style={{ backgroundColor: tag.color }} />
          <span className={styles.tagName}>{tag.name || 'Default color tag'}</span>
        </div>
      ),
      id: `${tag.name},${tag.color}`,
      onClick: () => handleCallback(tag),
    })),
    { type: 'spacer' },
    {
      label: 'Edit color tags',
      icon: 'pencil',
      onClick: () => toggleModal({ modalName: 'colorTags', flag: true }),
    },
  ]

  return [options, handleFetchGlobalColorTags]
}

export default useDeckColorTagOptions
